import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Snowflake, Briefcase, Storefront, WhatsappLogo } from "@phosphor-icons/react";
import { useMedia } from "@/context/MediaContext";
import { resolveImg } from "@/lib/api";

const ICONS = [Snowflake, Briefcase, Storefront];

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] } }),
};

export default function Oportunidades() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });
  const { oportunidades } = useMedia();
  const items = (oportunidades || []).filter((o) => o && o.nome);

  if (!items.length) return null;

  return (
    <section id="oportunidades" ref={ref} className="relative bg-vm-bg py-24 md:py-32" data-testid="oportunidades-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.p initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}} transition={{ duration: 0.6 }} className="text-xs tracking-[0.4em] uppercase text-vm-gold mb-6">
          Oportunidades Prontas
        </motion.p>
        <motion.h2 initial={{ opacity: 0, y: 30 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8 }} className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] max-w-3xl" data-testid="oportunidades-title">
          Espaços <span className="gold-text italic">Prontos para Começar</span>
        </motion.h2>

        <div className="mt-14 grid md:grid-cols-2 gap-6">
          {items.map((o, i) => {
            const Icon = ICONS[i % ICONS.length];
            return (
              <motion.div
                key={`${o.nome}-${i}`}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                animate={inView ? "visible" : "hidden"}
                className="group relative rounded-2xl border border-vm-border bg-vm-surface overflow-hidden hover:border-vm-gold/40 transition-colors duration-500 gold-glow flex flex-col"
                data-testid={`oportunidade-${i}`}
              >
                {o.image && (
                  <div className="relative aspect-[16/9] overflow-hidden">
                    <img src={resolveImg(o.image)} alt={o.nome} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-vm-surface via-transparent to-transparent" />
                  </div>
                )}

                <div className="p-8 md:p-10 flex flex-col flex-1">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <span className="text-xs tracking-[0.25em] uppercase text-vm-mutedFg">{o.tag}</span>
                      <h3 className="font-serif text-3xl mt-2">{o.nome}</h3>
                    </div>
                    <Icon size={40} weight="duotone" className="text-vm-gold shrink-0" />
                  </div>

                  <div className="mt-6 flex items-baseline gap-2">
                    <span className="font-serif text-4xl gold-text">{o.valor}</span>
                    <span className="text-sm text-vm-mutedFg">/mês</span>
                  </div>

                  {o.destaque && (
                    <div className="mt-6 inline-flex items-center gap-2 self-start rounded-full bg-vm-gold/10 border border-vm-gold/30 px-3 py-1.5">
                      <span className="h-1.5 w-1.5 rounded-full bg-vm-gold" />
                      <span className="text-xs text-vm-gold font-medium">{o.destaque}</span>
                    </div>
                  )}

                  <p className="mt-6 text-vm-mutedFg leading-relaxed flex-1">{o.descricao}</p>

                  <a
                    href={`https://wa.me/5521987356892?text=${encodeURIComponent(o.wa || `Olá! Tenho interesse na ${o.nome} do VM Shopping.`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-sweep relative z-0 mt-8 inline-flex items-center justify-center gap-2 bg-vm-gold text-vm-bg hover:text-vm-bg font-semibold px-6 py-3.5 rounded-full"
                    data-testid={`oportunidade-cta-${i}`}
                  >
                    Quero esta unidade
                    <WhatsappLogo size={18} weight="fill" />
                  </a>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
